import { useState } from 'react';
import { BedDouble, CheckCircle2, ChevronLeft, ChevronRight, House, MapPin, Maximize, Users } from 'lucide-react';
import { formatLabel, formatPrice, getCurrencySymbol, getImageUrl, getPriceUnit } from './accommodationFormatters';
import './accommodationSections.css';
import { leftFor, useCampAvailabilityView } from '../../hooks/useCampAvailability';

/*
 * The nine per-tab section components below (and the styling they
 * depend on, accommodationSections.css) used to live only inside
 * SupOrgAccommodationDetails.jsx. They're moved here, unchanged, so the
 * customer-facing camp details page (CampDetails.jsx) can render the
 * exact same content the support/org side does - previously it used a
 * different component entirely (ReviewSubmit, built for the org-side
 * creation wizard) for its own, differently-laid-out version of the
 * same accommodation data. Both pages now render identically; which
 * tabs/sections a given audience (user vs support vs org) actually
 * gets to see is a separate decision for later, not handled here yet.
 */

/* =========================================================
   OVERVIEW
========================================================= */

function OverviewSection({ packData }) {
    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>PROPERTY OVERVIEW</span>

                <h2>About this property</h2>

                <p>{packData.description || packData.overview || 'No description available.'}</p>
            </div>

            {packData.propertyHighlights?.length > 0 && (
                <div className='accSection-block'>
                    <h3>Property Highlights</h3>

                    <div className='highlightGrid'>
                        {packData.propertyHighlights.map((item, index) => (
                            <div key={index}>
                                <CheckCircle2 size={18} />

                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {packData.overview && (
                <div className='overviewDescription'>
                    <h3>Short Description</h3>

                    <p>{packData.overview}</p>
                </div>
            )}
        </section>
    );
}

/* =========================================================
   UNITS
========================================================= */

// "2026-10-07" -> "7 Oct"
const shortDate = (day) => new Date(`${day}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function UnitsSection({ packData }) {
    const units = packData?.units || [];

    // what's free on the dates picked in the camp booking tab (customer page only)
    const availability = useCampAvailabilityView(packData?._id);
    const datesText = availability?.checkin ? `${shortDate(availability.checkin)} – ${shortDate(availability.checkout)}` : '';

    /*
     * Stores the currently selected image for each unit.
     * Example:
     * {
     *   "unitId1": 0,
     *   "unitId2": 2
     * }
     */
    const [activeImages, setActiveImages] = useState({});

    const getUnitImages = (unit) => {
        const unitImages = (unit?.images || []).map((image) => getImageUrl(image)).filter(Boolean);

        /*
         * If the unit doesn't have its own images,
         * fall back to the property's main media.
         */
        if (unitImages.length > 0) {
            return unitImages;
        }

        return (packData?.media || []).map((image) => getImageUrl(image)).filter(Boolean);
    };

    const changeImage = (unitId, direction, totalImages) => {
        if (!totalImages) return;

        setActiveImages((prev) => {
            const currentIndex = prev[unitId] || 0;

            let nextIndex;

            if (direction === 'next') {
                nextIndex = currentIndex + 1 >= totalImages ? 0 : currentIndex + 1;
            } else {
                nextIndex = currentIndex - 1 < 0 ? totalImages - 1 : currentIndex - 1;
            }

            return {
                ...prev,
                [unitId]: nextIndex
            };
        });
    };

    const selectImage = (unitId, index) => {
        setActiveImages((prev) => ({
            ...prev,
            [unitId]: index
        }));
    };

    return (
        <section className='accSection'>
            {/* =================================================
                HEADING
            ================================================= */}

            <div className='accSection-heading'>
                <span>ROOMS & UNITS</span>

                <h2>Accommodation options</h2>

                <p>Explore the different units available at this property.</p>
            </div>

            {/* =================================================
                EMPTY
            ================================================= */}

            {units.length === 0 ? (
                <div className='emptyTab'>No accommodation units added.</div>
            ) : (
                <div className='unitDetailsList'>
                    {units.map((unit, index) => {
                        const pricing = unit?.pricing?.[0];

                        const price = pricing?.actualPrice ?? pricing?.basePrice ?? 0;

                        const unitId = unit?._id || `unit-${index}`;

                        const images = getUnitImages(unit);

                        const activeIndex = activeImages[unitId] || 0;

                        const activeImage = images[activeIndex] || null;

                        const totalGuests = unit?.occupancy?.total ?? unit?.maximumGuests ?? 0;

                        const totalBeds = unit?.beds?.reduce((total, bed) => total + (Number(bed?.count) || 0), 0) || 0;

                        const bathroomCount = unit?.bathrooms?.count || 0;

                        const stockUnits = unit?.inventory?.activeUnits ?? unit?.inventory?.totalUnits ?? 0;

                        // free on the dates picked in the booking tab (null: no dates yet)
                        const left = leftFor(availability, unit?._id);

                        /*
                         * Show maximum 4 thumbnails.
                         * The remaining images are represented by +N.
                         */
                        const thumbnailImages = images.slice(0, 4);

                        const remainingImages = Math.max(images.length - 4, 0);

                        return (
                            <div className='unitDetailsCard' key={unitId}>
                                {/* =================================================
                                    LEFT - PHOTO GALLERY
                                ================================================= */}

                                <div className='unitDetailsGallery'>
                                    {/* MAIN IMAGE */}

                                    <div className='unitDetailsMainImage'>
                                        {activeImage ? (
                                            <img src={activeImage} alt={unit?.name || 'Accommodation'} />
                                        ) : (
                                            <div className='unitDetailsNoImage'>No Image</div>
                                        )}

                                        {/* LEFT ARROW */}

                                        {images.length > 1 && (
                                            <button
                                                type='button'
                                                className='unitGalleryArrow left'
                                                onClick={() => changeImage(unitId, 'prev', images.length)}
                                            >
                                                <ChevronLeft size={18} />
                                            </button>
                                        )}

                                        {/* RIGHT ARROW */}

                                        {images.length > 1 && (
                                            <button
                                                type='button'
                                                className='unitGalleryArrow right'
                                                onClick={() => changeImage(unitId, 'next', images.length)}
                                            >
                                                <ChevronRight size={18} />
                                            </button>
                                        )}

                                        {/* IMAGE COUNT */}

                                        {images.length > 0 && (
                                            <span className='unitImageCount'>
                                                {activeIndex + 1}/{images.length}
                                            </span>
                                        )}
                                    </div>

                                    {/* THUMBNAILS */}

                                    {images.length > 1 && (
                                        <div className='unitDetailsThumbnails'>
                                            {thumbnailImages.map((image, imageIndex) => {
                                                const isActive = imageIndex === activeIndex;

                                                /*
                                                 * If there are more
                                                 * than 4 images, put
                                                 * the +N overlay on
                                                 * the last thumbnail.
                                                 */
                                                const showRemaining = imageIndex === 3 && remainingImages > 0;

                                                return (
                                                    <button
                                                        type='button'
                                                        key={`${unitId}-${imageIndex}`}
                                                        className={`unitDetailsThumbnail ${isActive ? 'active' : ''}`}
                                                        onClick={() => selectImage(unitId, imageIndex)}
                                                    >
                                                        <img
                                                            src={image}
                                                            alt={`${unit?.name || 'Unit'} ${imageIndex + 1}`}
                                                        />

                                                        {showRemaining && (
                                                            <span className='unitMoreImages'>+{remainingImages}</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* =================================================
                                    RIGHT - UNIT DETAILS
                                ================================================= */}

                                <div className='unitDetailsBody'>
                                    {/* TOP */}

                                    <div className='unitDetailsTop'>
                                        <div>
                                            <h3>{unit?.name || 'Accommodation Unit'}</h3>

                                            {unit?.shortName && <span>{unit.shortName}</span>}
                                        </div>

                                        {/* PRICE */}

                                        <div className='unitDetailsPrice'>
                                            <strong>
                                                {getCurrencySymbol(pricing?.currency)}

                                                {formatPrice(price)}
                                            </strong>

                                            <span>/ {getPriceUnit(pricing?.priceUnit)}</span>
                                        </div>
                                    </div>

                                    {/* DESCRIPTION */}

                                    {unit?.description && <p>{unit.description}</p>}

                                    {/* FACTS */}

                                    <div className='unitDetailsFacts'>
                                        <span>
                                            <Users size={17} />
                                            {totalGuests} Guests
                                        </span>

                                        <span>
                                            <BedDouble size={17} />
                                            {totalBeds} Beds
                                        </span>

                                        <span>
                                            <House size={17} />
                                            {bathroomCount} Bathroom
                                            {bathroomCount !== 1 ? 's' : ''}
                                        </span>

                                        {unit?.sizeSqft > 0 && (
                                            <span>
                                                <Maximize size={17} />
                                                {unit.sizeSqft} sq.ft
                                            </span>
                                        )}
                                    </div>

                                    {/* BOTTOM */}

                                    <div className='unitDetailsBottom'>
                                        {left === null ? (
                                            <span className='unitInventory'>
                                                {stockUnits} {stockUnits === 1 ? 'unit' : 'units'} at this property
                                            </span>
                                        ) : (
                                            <span className={`unitInventory ${left === 0 ? 'soldOut' : left <= availability.fewLeft ? 'few' : ''}`}>
                                                {left === 0
                                                    ? 'Sold out for your dates'
                                                    : left <= availability.fewLeft
                                                      ? `Only ${left} left for ${datesText}`
                                                      : `${left} available for ${datesText}`}
                                            </span>
                                        )}

                                        {unit?.sharingType && <span>{formatLabel(unit.sharingType)}</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

/* =========================================================
   AMENITIES
========================================================= */

function AmenitiesSection({ packData }) {
    const amenities = packData.amenities || {};

    const categories = Object.entries(amenities).filter(([, values]) => Array.isArray(values) && values.length > 0);

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>AMENITIES</span>

                <h2>Everything you need</h2>

                <p>Explore the facilities and amenities available at this property.</p>
            </div>

            {categories.length === 0 ? (
                <div className='emptyTab'>No amenities added.</div>
            ) : (
                <div className='amenitiesCategoryGrid'>
                    {categories.map(([category, values]) => (
                        <div className='amenityCategory' key={category}>
                            <h3>{formatLabel(category)}</h3>

                            <div>
                                {[...new Set(values)].map((item, index) => (
                                    <span key={index}>
                                        <CheckCircle2 size={16} />

                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/* =========================================================
   PRICING
========================================================= */

function PricingSection({ packData }) {
    const units = packData.units || [];

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>PRICING</span>

                <h2>Pricing & plans</h2>

                <p>Pricing configured for each accommodation unit.</p>
            </div>

            {units.length === 0 ? (
                <div className='emptyTab'>No pricing available.</div>
            ) : (
                units.map((unit, unitIndex) => (
                    <div className='pricingUnit' key={unit._id || unitIndex}>
                        <h3>{unit.name || 'Accommodation Unit'}</h3>

                        {unit.pricing?.length ? (
                            unit.pricing.map((pricing, index) => (
                                <div className='pricingRow' key={pricing._id || index}>
                                    <div>
                                        <strong>{pricing.name}</strong>

                                        <span>{formatLabel(pricing.mealPlan)}</span>

                                        <span>Includes {pricing.guestsIncluded} guests</span>
                                    </div>

                                    <div className='pricingAmount'>
                                        <strong>
                                            {getCurrencySymbol(pricing.currency)}
                                            {formatPrice(pricing.actualPrice ?? pricing.basePrice)}
                                        </strong>

                                        <span>/ {getPriceUnit(pricing.priceUnit)}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className='emptyPricing'>No pricing configured.</div>
                        )}
                    </div>
                ))
            )}
        </section>
    );
}

/* =========================================================
   SERVICES
========================================================= */

function ServicesSection({ packData }) {
    const services = packData.services || [];

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>SERVICES</span>

                <h2>Services available</h2>
            </div>

            {services.length === 0 ? (
                <div className='emptyTab'>No additional services added.</div>
            ) : (
                <div className='serviceGrid'>
                    {services.map((service, index) => (
                        <div className='serviceCard' key={service._id || index}>
                            <h3>{service.name}</h3>

                            <p>{service.description || 'Service available at this property.'}</p>

                            <div>
                                <strong>₹{formatPrice(service.price)}</strong>

                                <span>/ {formatLabel(service.priceUnit)}</span>
                            </div>

                            {service.optional && <small>Optional service</small>}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/* =========================================================
   POLICIES
========================================================= */

function PoliciesSection({ packData }) {
    const policies = packData.policies || {};

    const houseRules = policies.houseRules || packData.propertyRules || {};

    const cancellation = policies.cancellation || {};

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>POLICIES</span>

                <h2>Stay policies</h2>
            </div>

            <div className='policyGrid'>
                {/* CHECK IN */}

                <div className='policyCard'>
                    <h3>Check-in & Check-out</h3>

                    <p>
                        <span>Check-in</span>

                        <strong>{policies.checkInTime || 'Not specified'}</strong>
                    </p>

                    <p>
                        <span>Check-out</span>

                        <strong>{policies.checkOutTime || 'Not specified'}</strong>
                    </p>

                    {policies.sameDayBooking !== undefined && (
                        <p>
                            <span>Same day booking</span>

                            <strong>{policies.sameDayBooking ? 'Allowed' : 'Not allowed'}</strong>
                        </p>
                    )}
                </div>

                {/* BOOKING */}

                <div className='policyCard'>
                    <h3>Booking</h3>

                    <p>
                        <span>Minimum stay</span>

                        <strong>{policies.minStayNights || 1} night(s)</strong>
                    </p>

                    <p>
                        <span>Advance booking</span>

                        <strong>{policies.advanceBookingDays ?? 0} days</strong>
                    </p>

                    {policies.instantBooking !== undefined && (
                        <p>
                            <span>Instant booking</span>

                            <strong>{policies.instantBooking ? 'Available' : 'Not available'}</strong>
                        </p>
                    )}
                </div>

                {/* HOUSE RULES */}

                <div className='policyCard'>
                    <h3>House Rules</h3>

                    <p>
                        <span>Smoking</span>

                        <strong>{houseRules.smokingAllowed ? 'Allowed' : 'Not allowed'}</strong>
                    </p>

                    <p>
                        <span>Pets</span>

                        <strong>{houseRules.petsAllowed ? 'Allowed' : 'Not allowed'}</strong>
                    </p>

                    <p>
                        <span>Children</span>

                        <strong>{houseRules.childrenAllowed ? 'Allowed' : 'Not allowed'}</strong>
                    </p>
                </div>
            </div>

            {/* CANCELLATION */}

            {cancellation && (
                <div className='cancellationCard'>
                    <h3>Cancellation Policy</h3>

                    <p>
                        {cancellation.description ||
                            formatLabel(cancellation.type) ||
                            'Cancellation policy not specified.'}
                    </p>

                    {cancellation.refundRules?.length > 0 && (
                        <div className='refundRules'>
                            {cancellation.refundRules.map((rule, index) => (
                                <div key={index}>
                                    <span>{rule.beforeDays} days before check-in</span>

                                    <strong>{rule.refundPercent}% refund</strong>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

/* =========================================================
   MEDIA
========================================================= */

function MediaSection({ packData }) {
    const media = [];

    if (Array.isArray(packData.media)) {
        packData.media.forEach((item) => {
            media.push(item);
        });
    }

    if (Array.isArray(packData.cardImages)) {
        packData.cardImages.forEach((item) => {
            media.push(item);
        });
    }

    const uniqueMedia = media.filter((item, index, array) => {
        const url = getImageUrl(item);

        return url && array.findIndex((other) => getImageUrl(other) === url) === index;
    });

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>MEDIA</span>

                <h2>Photos & Videos</h2>

                <p>Property photos and media.</p>
            </div>

            {uniqueMedia.length === 0 ? (
                <div className='emptyTab'>No photos or videos added.</div>
            ) : (
                <div className='mediaDetailsGrid'>
                    {uniqueMedia.map((item, index) => {
                        const url = getImageUrl(item);

                        const isVideo = item?.type === 'video';

                        return (
                            <div className='mediaDetailsItem' key={`${url}-${index}`}>
                                {isVideo ? <video src={url} controls /> : <img src={url} alt='' />}
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

/* =========================================================
   LOCATION
========================================================= */

function LocationSection({ packData }) {
    const address = packData?.address || {};
    const nearby = packData?.nearbyPlaces || [];

    const coordinates = address?.geo?.coordinates || [];

    const hasCoordinates = coordinates.length === 2 && coordinates[0] != null && coordinates[1] != null;

    const longitude = hasCoordinates ? coordinates[0] : null;
    const latitude = hasCoordinates ? coordinates[1] : null;

    const googleMapsUrl = hasCoordinates
        ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
        : null;

    const mapUrl = hasCoordinates ? `https://www.google.com/maps?q=${latitude},${longitude}&z=14&output=embed` : null;

    return (
        <section className='accSection'>
            {/* =================================================
                HEADING
            ================================================= */}

            <div className='accSection-heading'>
                <span>LOCATION</span>

                <h2>Where you'll stay</h2>

                <p>Property address and nearby places.</p>
            </div>

            {/* =================================================
                ADDRESS
            ================================================= */}

            <div className='locationDetailsCard'>
                <MapPin size={22} />

                <div>
                    <h3>{address.line1 || 'Address not available'}</h3>

                    <p>
                        {[
                            address.line2,
                            address.locality,
                            address.city,
                            address.district,
                            address.state,
                            address.country,
                            address.pincode
                        ]
                            .filter(Boolean)
                            .join(', ')}
                    </p>

                    {address.landmark && <span>Landmark: {address.landmark}</span>}
                </div>
            </div>

            {/* =================================================
                MAP + NEARBY PLACES
            ================================================= */}

            <div className='locationMapLayout'>
                {/* =================================================
                    MAP
                ================================================= */}

                <div className='locationMapCard'>
                    <div className='locationMapHeader'>
                        <div>
                            <h3>Property location</h3>

                            <span>See where the accommodation is located</span>
                        </div>

                        {googleMapsUrl && (
                            <a
                                href={googleMapsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='locationMapButton'
                            >
                                Open in Maps
                            </a>
                        )}
                    </div>

                    {mapUrl ? (
                        <iframe
                            title='Accommodation location'
                            className='locationMapIframe'
                            src={mapUrl}
                            loading='lazy'
                            referrerPolicy='no-referrer-when-downgrade'
                        />
                    ) : (
                        <div className='locationMapEmpty'>Location coordinates are not available.</div>
                    )}
                </div>

                {/* =================================================
                    NEARBY PLACES
                ================================================= */}

                <div className='nearbyPlaces'>
                    <h3>Nearby places</h3>

                    {nearby.length > 0 ? (
                        <div className='nearbyPlacesList'>
                            {nearby.map((place, index) => (
                                <div className='nearbyPlace' key={place._id || index}>
                                    <MapPin size={17} />

                                    <div>
                                        <span>{place.name}</span>

                                        {place.type && <small>{place.type}</small>}
                                    </div>

                                    {place.distanceKm !== undefined && place.distanceKm !== null && (
                                        <strong>{place.distanceKm} km</strong>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className='emptyTab'>No nearby places added.</div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* =========================================================
   ADDITIONAL
========================================================= */

function AdditionalSection({ packData }) {
    const languages = packData.languagesSpoken || [];

    const highlights = packData.propertyHighlights || [];

    const tags = packData.tags || [];

    return (
        <section className='accSection'>
            <div className='accSection-heading'>
                <span>ADDITIONAL INFORMATION</span>

                <h2>More about this property</h2>
            </div>

            <div className='additionalGrid'>
                {/* LANGUAGES */}

                <div className='additionalCard'>
                    <h3>Languages Spoken</h3>

                    {languages.length > 0 ? (
                        <div className='tagList'>
                            {languages.map((language, index) => (
                                <span key={index}>{language}</span>
                            ))}
                        </div>
                    ) : (
                        <p>No languages specified.</p>
                    )}
                </div>

                {/* HIGHLIGHTS */}

                <div className='additionalCard'>
                    <h3>Property Highlights</h3>

                    {highlights.length > 0 ? (
                        <div className='tagList'>
                            {highlights.map((item, index) => (
                                <span key={index}>{item}</span>
                            ))}
                        </div>
                    ) : (
                        <p>No highlights added.</p>
                    )}
                </div>

                {/* TAGS */}

                {tags.length > 0 && (
                    <div className='additionalCard full'>
                        <h3>Tags</h3>

                        <div className='tagList'>
                            {tags.map((tag, index) => (
                                <span key={index}>{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* OVERVIEW */}

                {packData.overview && (
                    <div className='additionalCard full'>
                        <h3>Property Overview</h3>

                        <p>{packData.overview}</p>
                    </div>
                )}

                {/* TERMS */}

                {packData.terms?.length > 0 && (
                    <div className='additionalCard full'>
                        <h3>Terms & Conditions</h3>

                        <ul className='additionalList'>
                            {packData.terms.map((term, index) => (
                                <li key={index}>{term}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
}

/*
 * Maps a tab id (from AccommodationTabBar's ACCOMMODATION_TABS) to its
 * section component. Both pages use this instead of repeating the same
 * switch, so adding/removing a tab only has to happen in one place.
 * When you later decide which of these to show per audience, this is
 * the natural place to add a `visibleTabs`-style filter - it already
 * knows how to map an id to its content, it just doesn't gate on
 * audience yet.
 */
export default function AccommodationSectionContent({ activeTab, packData }) {
    switch (activeTab) {
        case 'basic':
            return <OverviewSection packData={packData} />;
        case 'units':
            return <UnitsSection packData={packData} />;
        case 'amenities':
            return <AmenitiesSection packData={packData} />;
        case 'pricing':
            return <PricingSection packData={packData} />;
        case 'services':
            return <ServicesSection packData={packData} />;
        case 'policies':
            return <PoliciesSection packData={packData} />;
        case 'media':
            return <MediaSection packData={packData} />;
        case 'location':
            return <LocationSection packData={packData} />;
        case 'additional':
            return <AdditionalSection packData={packData} />;
        default:
            return null;
    }
}

export {
    OverviewSection,
    UnitsSection,
    AmenitiesSection,
    PricingSection,
    ServicesSection,
    PoliciesSection,
    MediaSection,
    LocationSection,
    AdditionalSection
};
